import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { CaretLeft, CaretRight, TrendUp, TrendDown, Scales, Receipt, Wrench, Lock, SignOut, DownloadSimple, UploadSimple, Trash, Wallet } from "@phosphor-icons/react";
import { toast } from "sonner";

const inputCls = "w-full px-3.5 py-2.5 rounded-md bg-background border border-input text-sm focus:outline-none focus:border-primary transition-colors duration-150";

const eur = (n) => `€ ${Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2 })}`;
const METHOD_LABELS = { contanti: "Contanti", carta: "Carta", bonifico: "Bonifico", assegno: "Assegno", finanziamento: "Finanziamento", bonus_docenti: "Bonus Docenti", cash: "Cash" };
const MONTHS = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

function Bar({ label, value, max, color, testid }) {
  const pct = max > 0 ? Math.max(3, (value / max) * 100) : 0;
  return (
    <div className="flex items-center gap-3" data-testid={testid}>
      <span className="text-xs w-28 sm:w-32 shrink-0 truncate">{label}</span>
      <div className="flex-1 h-5 rounded bg-secondary overflow-hidden">
        <div className={`h-full rounded ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs font-bold w-24 text-right shrink-0">{eur(value)}</span>
    </div>
  );
}

function Section({ title, children, testid }) {
  return (
    <div className="border border-border rounded-md bg-card" data-testid={testid}>
      <div className="px-5 py-4 border-b border-border">
        <h2 className="font-heading font-bold tracking-tight">{title}</h2>
      </div>
      <div className="p-5 space-y-3">{children}</div>
    </div>
  );
}

export default function Statistiche() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [token, setToken] = useState(() => sessionStorage.getItem("stats_token") || "");
  const [pw, setPw] = useState("");
  const [pwError, setPwError] = useState(false);
  const [mode, setMode] = useState("mese");
  const [dateFrom, setDateFrom] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`);
  const [dateTo, setDateTo] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`);

  useEffect(() => () => sessionStorage.removeItem("stats_token"), []);

  useEffect(() => {
    if (!token) return;
    if (mode === "periodo" && (!dateFrom || !dateTo || dateTo < dateFrom)) return;
    setData(null);
    const params = mode === "mese" ? { year, month } : { date_from: dateFrom, date_to: dateTo };
    api.get("/stats/monthly", { params, headers: { "X-Stats-Token": token } })
      .then((r) => setData(r.data))
      .catch((err) => {
        if ([401, 403].includes(err?.response?.status)) {
          sessionStorage.removeItem("stats_token");
          setToken("");
        }
      });
  }, [year, month, token, mode, dateFrom, dateTo]);

  const unlock = async (e) => {
    e.preventDefault();
    try {
      const { data: res } = await api.post("/stats/unlock", { password: pw });
      sessionStorage.setItem("stats_token", res.stats_token);
      setToken(res.stats_token);
      setPw("");
      setPwError(false);
    } catch (err) {
      setPwError(err?.response?.data?.detail || "Password errata, riprova.");
    }
  };

  const exit = () => {
    sessionStorage.removeItem("stats_token");
    setToken("");
    navigate("/");
  };

  const shift = (d) => {
    let m = month + d;
    let y = year;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setMonth(m);
    setYear(y);
  };

  const fileRef = useRef(null);
  const [busy, setBusy] = useState("");

  const downloadBackup = async () => {
    setBusy("export");
    try {
      const r = await api.get("/backup/export", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `techgest-backup-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Backup scaricato");
    } catch {
      toast.error("Errore durante il backup");
    } finally {
      setBusy("");
    }
  };

  const restoreBackup = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!window.confirm(`Caricare il backup "${file.name}"?\n\nATTENZIONE: tutti i dati attuali verranno SOSTITUITI con quelli del file.`)) return;
    setBusy("restore");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: res } = await api.post("/backup/restore", fd);
      const tot = Object.values(res.restored || {}).reduce((a, b) => a + b, 0);
      toast.success(`Backup ripristinato: ${tot} documenti`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore durante il ripristino");
    } finally {
      setBusy("");
    }
  };

  const wipeData = async () => {
    if (!window.confirm("ATTENZIONE: stai per cancellare TUTTI i dati (clienti, riparazioni, ordini, prima nota...).\n\nGli utenti NON vengono cancellati.\n\nVuoi continuare?")) return;
    if (window.prompt("Per confermare scrivi CANCELLA") !== "CANCELLA") {
      toast.error("Cancellazione annullata");
      return;
    }
    setBusy("wipe");
    try {
      const { data: res } = await api.post("/backup/wipe");
      const tot = Object.values(res.wiped || {}).reduce((a, b) => a + b, 0);
      toast.success(`Dati cancellati: ${tot} documenti rimossi`);
    } catch {
      toast.error("Errore durante la cancellazione");
    } finally {
      setBusy("");
    }
  };

  const delta = (cur, prev) => {
    const diff = cur - prev;
    return (
      <span className={`text-xs font-bold ${diff >= 0 ? "text-green-500" : "text-red-500"}`}>
        {diff >= 0 ? "+" : "−"}{eur(Math.abs(diff))} vs mese scorso
      </span>
    );
  };

  const methodEntries = (obj) => Object.entries(obj || {}).sort((a, b) => b[1] - a[1]);
  const maxMethod = data ? Math.max(1, ...methodEntries(data.by_method_entrate).map(([, v]) => v), ...methodEntries(data.by_method_uscite).map(([, v]) => v)) : 1;
  const maxCatE = data && data.by_category_entrate.length ? data.by_category_entrate[0].total : 1;
  const maxCatU = data && data.by_category_uscite.length ? data.by_category_uscite[0].total : 1;

  if (!token) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[70vh]" data-testid="stats-lock-screen">
        <div className="w-full max-w-sm border border-border rounded-md bg-card p-8 text-center space-y-5">
          <div className="w-14 h-14 mx-auto rounded-full bg-primary/15 text-primary flex items-center justify-center">
            <Lock size={26} weight="duotone" />
          </div>
          <div>
            <h1 className="font-heading text-xl font-extrabold tracking-tight">Area riservata</h1>
            <p className="text-sm text-muted-foreground mt-1">Inserisci la password per consultare le statistiche.</p>
          </div>
          <form onSubmit={unlock} className="space-y-3">
            <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Password"
              data-testid="stats-password-input" className={inputCls} autoFocus />
            {pwError && <p className="text-xs text-red-500 text-left" data-testid="stats-lock-error">{pwError}</p>}
            <button type="submit" data-testid="stats-unlock-button"
              className="w-full py-3 rounded-md bg-primary text-primary-foreground font-semibold text-sm hover:opacity-90 transition-opacity duration-150">
              Sblocca statistiche
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="statistiche-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-1">Solo admin</p>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Statistiche</h1>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={exit} data-testid="stats-exit-button"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-border text-sm font-semibold text-muted-foreground hover:text-destructive hover:bg-accent transition-colors duration-150">
            <SignOut size={15} /> Esci
          </button>
          <div className="flex items-center gap-1 border border-border rounded-md p-0.5" data-testid="stats-mode-toggle">
            <button onClick={() => setMode("mese")} data-testid="stats-mode-mese"
              className={`px-3 py-1.5 rounded text-xs font-bold transition-colors duration-150 ${mode === "mese" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
              Mese
            </button>
            <button onClick={() => setMode("periodo")} data-testid="stats-mode-periodo"
              className={`px-3 py-1.5 rounded text-xs font-bold transition-colors duration-150 ${mode === "periodo" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
              Periodo
            </button>
          </div>
          {mode === "mese" ? (
          <div className="flex items-center gap-2" data-testid="month-nav">
          <button onClick={() => shift(-1)} data-testid="month-prev"
            className="w-9 h-9 rounded-md border border-border inline-flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-accent transition-colors duration-150">
            <CaretLeft size={15} />
          </button>
          <span className="font-heading font-bold text-lg min-w-[180px] text-center" data-testid="month-label">
            {MONTHS[month - 1]} {year}
          </span>
          <button onClick={() => shift(1)} data-testid="month-next"
            className="w-9 h-9 rounded-md border border-border inline-flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-accent transition-colors duration-150">
            <CaretRight size={15} />
          </button>
          </div>
          ) : (
          <div className="flex items-center gap-2" data-testid="range-nav">
            <span className="text-xs font-semibold text-muted-foreground">Da</span>
            <input type="date" value={dateFrom} max={dateTo} onChange={(e) => setDateFrom(e.target.value)}
              data-testid="stats-date-from" className="px-2 py-1.5 rounded-md border border-border bg-background text-sm" />
            <span className="text-xs font-semibold text-muted-foreground">A</span>
            <input type="date" value={dateTo} min={dateFrom} onChange={(e) => setDateTo(e.target.value)}
              data-testid="stats-date-to" className="px-2 py-1.5 rounded-md border border-border bg-background text-sm" />
          </div>
          )}
        </div>
      </div>

      {!data ? (
        <div className="py-16 flex justify-center" data-testid="stats-loading">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="stats-summary">
            <div className="border border-border rounded-lg bg-card p-5">
              <p className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-2 flex items-center gap-1.5">
                <TrendUp size={13} className="text-green-500" /> Entrate del mese
              </p>
              <p className="font-heading text-3xl font-extrabold text-green-500" data-testid="stats-entrate">{eur(data.entrate)}</p>
              {(data.da_incassare || 0) > 0 && (
                <p className="text-xs font-semibold text-amber-500 mt-1" data-testid="stats-da-incassare">di cui da incassare: {eur(data.da_incassare)}</p>
              )}
              {delta(data.entrate, data.prev_entrate)}
            </div>
            <div className="border border-border rounded-lg bg-card p-5">
              <p className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-2 flex items-center gap-1.5">
                <TrendDown size={13} className="text-red-500" /> Uscite del mese
              </p>
              <p className="font-heading text-3xl font-extrabold text-red-500" data-testid="stats-uscite">{eur(data.uscite)}</p>
              {delta(data.uscite, data.prev_uscite)}
            </div>
            <div className="border border-border rounded-lg bg-card p-5">
              <p className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-2 flex items-center gap-1.5">
                <Scales size={13} /> Saldo
              </p>
              <p className={`font-heading text-3xl font-extrabold ${data.saldo >= 0 ? "text-foreground" : "text-red-500"}`} data-testid="stats-saldo">{eur(data.saldo)}</p>
              <span className="text-xs text-muted-foreground">entrate − uscite</span>
            </div>
            <div className="border border-border rounded-lg bg-card p-5">
              <p className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-2 flex items-center gap-1.5">
                <Wallet size={13} className="text-amber-500" /> Cash (non contabilizzato)
              </p>
              <p className={`font-heading text-3xl font-extrabold ${(data.cash_netto || 0) >= 0 ? "text-amber-500" : "text-red-500"}`} data-testid="stats-cash-netto">{eur(data.cash_netto || 0)}</p>
              <span className="text-xs text-muted-foreground" data-testid="stats-cash-detail">entrate {eur(data.cash_entrate || 0)} · uscite {eur(data.cash_uscite || 0)}</span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Section title="Entrate per metodo di pagamento" testid="stats-methods-entrate">
              {methodEntries(data.by_method_entrate).length === 0 && <p className="text-sm text-muted-foreground">Nessuna entrata nel mese.</p>}
              {methodEntries(data.by_method_entrate).map(([m, v]) => (
                <Bar key={m} label={METHOD_LABELS[m] || m} value={v} max={maxMethod} color="bg-green-500/70" testid={`bar-entrata-${m}`} />
              ))}
            </Section>
            <Section title="Uscite per metodo di pagamento" testid="stats-methods-uscite">
              {methodEntries(data.by_method_uscite).length === 0 && <p className="text-sm text-muted-foreground">Nessuna uscita nel mese.</p>}
              {methodEntries(data.by_method_uscite).map(([m, v]) => (
                <Bar key={m} label={METHOD_LABELS[m] || m} value={v} max={maxMethod} color="bg-red-500/70" testid={`bar-uscita-${m}`} />
              ))}
            </Section>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Section title="Entrate per categoria" testid="stats-categories-entrate">
              {data.by_category_entrate.length === 0 && <p className="text-sm text-muted-foreground">Nessuna entrata nel mese.</p>}
              {data.by_category_entrate.map((c) => (
                <Bar key={c.name} label={c.name} value={c.total} max={maxCatE} color="bg-green-500/70" testid={`bar-cat-e-${c.name.toLowerCase().replace(/\s+/g, "-")}`} />
              ))}
            </Section>
            <Section title="Uscite per categoria" testid="stats-categories-uscite">
              {data.by_category_uscite.length === 0 && <p className="text-sm text-muted-foreground">Nessuna uscita nel mese.</p>}
              {data.by_category_uscite.map((c) => (
                <Bar key={c.name} label={c.name} value={c.total} max={maxCatU} color="bg-red-500/70" testid={`bar-cat-u-${c.name.toLowerCase().replace(/\s+/g, "-")}`} />
              ))}
            </Section>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="border border-border rounded-lg bg-card p-5" data-testid="stats-scontrino">
              <p className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-2 flex items-center gap-1.5">
                <Receipt size={13} /> Scontrini
              </p>
              <p className="font-heading text-2xl font-extrabold">{data.receipts.scontrino.count}</p>
              <p className="text-sm text-muted-foreground mt-1">{eur(data.receipts.scontrino.total)}</p>
            </div>
            <div className="border border-border rounded-lg bg-card p-5" data-testid="stats-fattura">
              <p className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-2 flex items-center gap-1.5">
                <Receipt size={13} /> Fatture
              </p>
              <p className="font-heading text-2xl font-extrabold">{data.receipts.fattura.count}</p>
              <p className="text-sm text-muted-foreground mt-1">{eur(data.receipts.fattura.total)}</p>
            </div>
            <div className="border border-border rounded-lg bg-card p-5" data-testid="stats-repairs">
              <p className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-2 flex items-center gap-1.5">
                <Wrench size={13} /> Riparazioni completate
              </p>
              <p className="font-heading text-2xl font-extrabold">{data.repairs_completed.count}</p>
              <p className="text-sm text-muted-foreground mt-1">
                {eur(data.repairs_completed.total)} · media {eur(data.repairs_completed.avg)}
              </p>
            </div>
          </div>
          <Section title="Backup dati" testid="backup-section">
            <p className="text-sm text-muted-foreground">
              Il backup include clienti, dispositivi, riparazioni, ordini, prenotazioni, prima nota, categorie, messaggi e utenti.
            </p>
            <div className="flex flex-wrap gap-2">
              <button onClick={downloadBackup} disabled={!!busy} data-testid="backup-download-button"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity duration-150 disabled:opacity-50">
                <DownloadSimple size={15} /> {busy === "export" ? "Preparazione..." : "Scarica backup"}
              </button>
              <button onClick={() => fileRef.current?.click()} disabled={!!busy} data-testid="backup-restore-button"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md border border-border text-sm font-semibold hover:bg-accent transition-colors duration-150 disabled:opacity-50">
                <UploadSimple size={15} /> {busy === "restore" ? "Ripristino..." : "Carica backup"}
              </button>
              <input ref={fileRef} type="file" accept="application/json,.json" onChange={restoreBackup} className="hidden" data-testid="backup-restore-input" />
              <button onClick={wipeData} disabled={!!busy} data-testid="backup-wipe-button"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md border border-red-500/40 text-red-500 text-sm font-semibold hover:bg-red-500/10 transition-colors duration-150 disabled:opacity-50">
                <Trash size={15} /> {busy === "wipe" ? "Cancellazione..." : "Cancella tutti i dati"}
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              Carica backup sostituisce tutti i dati attuali. Cancella tutti i dati richiede doppia conferma e non elimina gli utenti.
            </p>
          </Section>
        </>
      )}
    </div>
  );
}
